"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zodInputJsonSchema = zodInputJsonSchema;
const zod_1 = require("zod");
/** String checks zod publishes as a `format` no JSON Schema draft defines. */
const NON_STANDARD_FORMATS = new Set([
    'starts_with',
    'ends_with',
    'includes',
]);
/**
 * zod string formats published as the JSON Schema format keyword alone, as
 * tool providers and the stored agent tool digests know them: without the
 * regular expression zod checks the format with.
 */
const STANDARD_STRING_FORMATS = {
    email: 'email',
    url: 'uri',
    uuid: 'uuid',
    guid: 'uuid',
};
/**
 * JSON Schema (draft-07) of the input a Zod schema accepts, for AI tool
 * providers. It speaks the subset providers share: objects that drop unknown
 * keys are published closed, loose ones open (`true`), unions as `anyOf`, no
 * zod-only formats, and a custom check piped into a schema publishes that
 * schema. A recursive schema lands once in `definitions` and is referenced
 * from there.
 */
function zodInputJsonSchema(schema) {
    const document = zod_1.z.toJSONSchema(schema, {
        target: 'draft-07',
        io: 'input',
        unrepresentable: 'any',
        override: ({ zodSchema, jsonSchema }) => {
            const def = zodSchema._zod.def;
            if (def.type === 'pipe') {
                const pipe = def;
                if (pipe.in._zod.def.type === 'custom') {
                    const published = zodInputJsonSchema(pipe.out);
                    if ('definitions' in published) {
                        throw new Error('A piped custom check must not wrap a recursive schema');
                    }
                    for (const key of Object.keys(jsonSchema))
                        delete jsonSchema[key];
                    Object.assign(jsonSchema, published);
                }
            }
            if (def.type === 'string')
                publishStandardFormats(zodSchema, jsonSchema);
            if (def.type === 'object') {
                if (jsonSchema.additionalProperties === undefined) {
                    jsonSchema.additionalProperties = false;
                }
                else if (isEmptySchema(jsonSchema.additionalProperties)) {
                    jsonSchema.additionalProperties = true;
                }
            }
            // JSON keys are strings: the keyword states nothing.
            const names = jsonSchema.propertyNames;
            if (typeof names === 'object' &&
                names.type === 'string' &&
                Object.keys(names).length === 1) {
                delete jsonSchema.propertyNames;
            }
            if (jsonSchema.oneOf) {
                jsonSchema.anyOf = jsonSchema.oneOf;
                delete jsonSchema.oneOf;
            }
            if (NON_STANDARD_FORMATS.has(jsonSchema.format))
                delete jsonSchema.format;
        },
    });
    delete document.$schema;
    return unwrapSingleAllOf(document);
}
function isEmptySchema(value) {
    return (value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0);
}
function publishStandardFormats(schema, json) {
    const def = schema._zod.def;
    // A format schema such as z.email() is its own first check.
    const checks = [schema, ...(def.checks ?? [])];
    for (const check of checks) {
        const checkDef = check._zod.def;
        if (checkDef.check !== 'string_format' || !checkDef.format)
            continue;
        const format = STANDARD_STRING_FORMATS[checkDef.format];
        if (!format)
            continue;
        json.format = format;
        if (checkDef.pattern)
            dropPattern(json, checkDef.pattern.source);
    }
}
function dropPattern(json, source) {
    if (json.pattern === source)
        delete json.pattern;
    if (!json.allOf)
        return;
    const rest = json.allOf.filter(part => typeof part !== 'object' || part.pattern !== source);
    if (rest.length > 1)
        json.allOf = rest;
    else {
        delete json.allOf;
        const [only] = rest;
        if (typeof only === 'object' && only.pattern)
            json.pattern = only.pattern;
    }
}
/** `{ allOf: [x], ...rest }` is `{ ...x, ...rest }` when no key collides. */
function unwrapSingleAllOf(value) {
    if (Array.isArray(value))
        return value.map(unwrapSingleAllOf);
    if (value === null || typeof value !== 'object')
        return value;
    const node = Object.fromEntries(Object.entries(value).map(([key, child]) => [key, unwrapSingleAllOf(child)]));
    const { allOf, ...rest } = node;
    if (!Array.isArray(allOf) ||
        allOf.length !== 1 ||
        allOf[0] === null ||
        typeof allOf[0] !== 'object' ||
        Object.keys(allOf[0]).some(key => key in rest)) {
        return node;
    }
    return { ...allOf[0], ...rest };
}
